import { apiClient, ApiClient } from "@/lib/api/api-client";
import {
  Category,
  Degree,
  Language,
  Level,
  Skill,
} from "@/modules/types/metadata/metadata";
import { BaseResponse } from "@/modules/types/response";

export interface GetSkillsResponse extends BaseResponse {
  skills: Skill[];
  nextId?: string;
  total: number;
}

export interface LevelsResponse extends BaseResponse {
  levels?: Level[];
  total?: number;
}

export interface DegreesResponse extends BaseResponse {
  degrees?: Degree[];
  total?: number;
}

export interface CategoriesResponse extends BaseResponse {
  categories?: Category[];
  total?: number;
}

export interface LanguagesResponse extends BaseResponse {
  languages?: Language[];
}

export class MetadataService {
  private BASE_PATH =
    process.env.API_METADATA_ENDPOINT || "http://localhost:8081/metadata";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getSkills({
    name,
    slug,
    categoryId,
    nextId,
    page,
    limit,
  }: {
    name?: string;
    slug?: string;
    categoryId?: string;
    nextId?: string;
    page?: number;
    limit?: number;
  }): Promise<GetSkillsResponse> {
    const params = new URLSearchParams();
    if (name) params.append("name", name);
    if (slug) params.append("slug", slug);
    if (categoryId) params.append("categoryId", categoryId);
    if (nextId) params.append("nextId", nextId);
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());

    return this.apiClient.fetch<GetSkillsResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/skills${params.toString() ? `?${params.toString()}` : ""}`,
      {
        cache: "force-cache",
      },
    );
  }

  async getLevels(): Promise<LevelsResponse> {
    return await this.apiClient.fetch<LevelsResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/levels`,
      {
        cache: "force-cache",
      },
    );
  }

  async getDegrees(): Promise<DegreesResponse> {
    return await this.apiClient.fetch<DegreesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/degrees`,
      {
        cache: "force-cache",
      },
    );
  }

  async getCategories(): Promise<CategoriesResponse> {
    return await this.apiClient.fetch<CategoriesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/categories`,
      {
        cache: "force-cache",
      },
    );
  }

  async getLanguages(): Promise<LanguagesResponse> {
    return await this.apiClient.fetch<LanguagesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/languages`,
      {
        cache: "force-cache",
      },
    );
  }
}

export const metadataService = new MetadataService();
